import type { AxiosError } from 'axios'

import { parseEditDetailedSummaryExpensesPayload } from '../helpers/parseEditDetailedSummaryExpensesPayload'
import { parseGetDetailedSummaryExpensesPayload } from '../helpers/parseGetDetailedSummaryExpensesPayload'
import { parseGetAdditionalExpensesPayload } from '../helpers/parseGetAdditionalExpensesPayload'
import { parseAdditionalExpensesPayload } from '../helpers/parseAdditionalExpensesPayload'
import { toast } from '@utils/functions/notificationToast'
import { useOtherExpenses } from '.'

import type { EditDetailedSummaryExpensesModalFormProps } from '../components/modals/EditDetailedSummaryExpenses/form/types'
import type { AddEditAdditionalExpensesModalFormProps } from '../components/modals/AddEditAdditionalExpenses/form/types'
import type {
  GetBigNumbersOtherExpensesRequestProps,
  PreviousOtherExpensesRequestProps,
  RemoveAdditionalExpensesRequestProps,
  ReverseWorkflowOtherExpensesRequestProps
} from '@services/api/otherExpenses/types'
import type { GetAdditionalExpensesFormProps, GetDetailedSummaryExpensesFormProps } from './types'

import * as apiOtherExpenses from '@services/api/otherExpenses'

const reversePreviewOtherExpenses = async (payload: PreviousOtherExpensesRequestProps) => {
  const { setIsReversePreviewOtherExpensesLoading } = useOtherExpenses.getState()

  setIsReversePreviewOtherExpensesLoading(true)

  try {
    await apiOtherExpenses.reversePreviewOtherExpenses(payload)

    toast.success('Prévia de outras despesas revertida com sucesso!')

    getBigNumbersOtherExpenses({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível reverter a prévia. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsReversePreviewOtherExpensesLoading(false)
  }
}

const releasePreviewOtherExpenses = async (payload: PreviousOtherExpensesRequestProps) => {
  const { setIsReleasePreviewOtherExpensesLoading } = useOtherExpenses.getState()

  setIsReleasePreviewOtherExpensesLoading(true)

  try {
    await apiOtherExpenses.releasePreviewOtherExpenses(payload)

    toast.success('Prévia liberada com sucesso!')

    getBigNumbersOtherExpenses({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    toast.error('Não foi possível liberar a prévia. Tente novamente', (error as AxiosError)?.status)

    return false
  } finally {
    setIsReleasePreviewOtherExpensesLoading(false)
  }
}

const reverseWorkflowOtherExpenses = async (payload: ReverseWorkflowOtherExpensesRequestProps) => {
  const { setIsReverseWorkflowOtherExpensesLoading } = useOtherExpenses.getState()

  setIsReverseWorkflowOtherExpensesLoading(true)

  try {
    await apiOtherExpenses.reverseWorkflowOtherExpenses(payload)

    toast.success('Prévia de outras despesas restaurada com sucesso!')

    getBigNumbersOtherExpenses({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao restaurar a prévia de outras despesas!'

    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsReverseWorkflowOtherExpensesLoading(false)
  }
}

const approvalWorkflowOtherExpenses = async (payload: any) => {
  const { setIsApprovalWorkflowOtherExpensesLoading } = useOtherExpenses.getState()

  setIsApprovalWorkflowOtherExpensesLoading(true)

  try {
    await apiOtherExpenses.approvalWorkflowOtherExpenses(payload)

    toast.success('Prévia de outras despesas enviada com sucesso!')

    getBigNumbersOtherExpenses({
      filiCode: payload.filiCode,
      competenceDate: payload.competenceDate
    })

    return true
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage[0] ||
      'Falha ao enviar a prévia de outras despesas!'

    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsApprovalWorkflowOtherExpensesLoading(false)
  }
}

const getBigNumbersOtherExpenses = async (payload: GetBigNumbersOtherExpensesRequestProps) => {
  const { setBigNumbersOtherExpensesData, setIsBigNumbersOtherExpensesLoading } = useOtherExpenses.getState()

  setIsBigNumbersOtherExpensesLoading(true)

  try {
    const data = await apiOtherExpenses.getBigNumbersOtherExpenses(payload)

    setBigNumbersOtherExpensesData(data)
  } catch (error) {
    toast.error('Falha ao buscar ao obter os dados!', (error as AxiosError)?.status)
  } finally {
    setIsBigNumbersOtherExpensesLoading(false)
  }
}

const editDetailedSummaryExpenses = async (formData: EditDetailedSummaryExpensesModalFormProps) => {
  const { setIsEditDetailedSummaryExpensesLoading } = useOtherExpenses.getState()

  setIsEditDetailedSummaryExpensesLoading(true)

  try {
    const payload = parseEditDetailedSummaryExpensesPayload(formData)

    await apiOtherExpenses.editDetailedSummaryExpenses(payload)

    toast.success('O Resumo Detalhado foi editado com sucesso!')

    getDetailedSummaryExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getBigNumbersOtherExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    return true
  } catch (error) {
    toast.error('Falha ao editar o resumo detalhado!', (error as AxiosError)?.status)

    return false
  } finally {
    setIsEditDetailedSummaryExpensesLoading(false)
  }
}

const getDetailedSummaryExpenses = async (formData: GetDetailedSummaryExpensesFormProps) => {
  const { setDetailedSummaryExpensesData, setIsDetailedSummaryExpensesLoading } = useOtherExpenses.getState()

  setIsDetailedSummaryExpensesLoading(true)

  try {
    const payload = parseGetDetailedSummaryExpensesPayload(formData)

    const data = await apiOtherExpenses.getDetailedSummaryExpenses(payload)

    setDetailedSummaryExpensesData(data)
  } catch (error) {
    toast.error('Falha ao buscar o resumo detalhado!', (error as AxiosError)?.status)
  } finally {
    setIsDetailedSummaryExpensesLoading(false)
  }
}

const removeAdditionalExpense = async (formData: RemoveAdditionalExpensesRequestProps) => {
  const { setIsRemoveAdditionalExpenseLoading } = useOtherExpenses.getState()

  setIsRemoveAdditionalExpenseLoading(true)

  const refetchLists = () => {
    getAdditionalExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getDetailedSummaryExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getBigNumbersOtherExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })
  }

  try {
    await apiOtherExpenses.removeAdditionalExpenses(formData)

    toast.success('A Despesa foi excluída com sucesso!')

    refetchLists()

    return true
  } catch (error) {
    const axiosError = error as AxiosError

    // ponytail: sem status HTTP (timeout/conexão caiu) não dá pra saber se o delete foi aplicado no backend;
    // atualiza as listas e deixa o estado real decidir, em vez de assumir falha. Erro de verdade (com status) ainda mostra o toast.
    if (!axiosError?.response) {
      refetchLists()

      return true
    }

    toast.error('Falha ao excluir a despesa!', axiosError?.status)

    return false
  } finally {
    setIsRemoveAdditionalExpenseLoading(false)
  }
}

const editAdditionalExpenses = async (formData: AddEditAdditionalExpensesModalFormProps) => {
  const { setIsAddEditAdditionalExpensesLoading } = useOtherExpenses.getState()

  setIsAddEditAdditionalExpensesLoading(true)

  try {
    const payload = parseAdditionalExpensesPayload(formData)

    await apiOtherExpenses.editAdditionalExpenses(payload)

    toast.success('A Despesa foi editada com sucesso!')

    getAdditionalExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getDetailedSummaryExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getBigNumbersOtherExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    return true
  } catch (error) {
    toast.error('Falha ao editar a despesa!', (error as AxiosError)?.status)

    return false
  } finally {
    setIsAddEditAdditionalExpensesLoading(false)
  }
}

const createAdditionalExpenses = async (formData: AddEditAdditionalExpensesModalFormProps) => {
  const { setIsAddEditAdditionalExpensesLoading } = useOtherExpenses.getState()

  setIsAddEditAdditionalExpensesLoading(true)

  try {
    const payload = parseAdditionalExpensesPayload(formData)

    await apiOtherExpenses.createAdditionalExpenses(payload)

    toast.success('A Despesa foi adicionada com sucesso!')

    getAdditionalExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getDetailedSummaryExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    getBigNumbersOtherExpenses({
      filiCode: formData.filiCode!,
      competenceDate: formData.competenceDate!
    })

    return true
  } catch (error) {
    toast.error('Falha ao adicionar a despesa!', (error as AxiosError)?.status)

    return false
  } finally {
    setIsAddEditAdditionalExpensesLoading(false)
  }
}

const getAdditionalExpenses = async (formData: GetAdditionalExpensesFormProps) => {
  const { setAdditionalExpensesData, setAdditionalExpensesLoading } = useOtherExpenses.getState()

  setAdditionalExpensesLoading(true)

  try {
    const payload = parseGetAdditionalExpensesPayload(formData)

    const data = await apiOtherExpenses.getAdditionalExpenses(payload)

    setAdditionalExpensesData(data)
  } catch (error) {
    toast.error('Falha ao buscar as despesas adicionais!', (error as AxiosError)?.status)
  } finally {
    setAdditionalExpensesLoading(false)
  }
}

export {
  getAdditionalExpenses,
  createAdditionalExpenses,
  editAdditionalExpenses,
  removeAdditionalExpense,
  getDetailedSummaryExpenses,
  editDetailedSummaryExpenses,
  getBigNumbersOtherExpenses,
  approvalWorkflowOtherExpenses,
  reverseWorkflowOtherExpenses,
  reversePreviewOtherExpenses,
  releasePreviewOtherExpenses
}
